import { useState, useEffect, useCallback, useRef } from "react";
import { getDeferredQuote, getQuote } from "../services/api";
import type {
  IQuoteResponse,
  IQuoteRequest,
  QuoteAnchor,
} from "../services/api";
import { getEvents, safeCapture } from "../analytics";
import {
  BINDING_QUOTE_DEBOUNCE_MS,
  isBindingQuoteReady,
  quoteUiOnIdentityChange,
} from "../utils/quoteMatchesSelection";

// Buffer de seguridad: refresh automático N segundos antes de expiración,
// para evitar que el quote expire mientras el usuario hace click en "Confirmar".
const EXPIRATION_BUFFER_SECONDS = 5;

// Umbral de advertencia: mostrar warning cuando quedan N segundos.
const WARNING_THRESHOLD_SECONDS = 10;

/** In-flight dedupe across hook instances (TransferPage + slots / StrictMode). */
const inFlight = new Map<string, Promise<IQuoteResponse>>();

const quoteKey = (request: IQuoteRequest): string => {
  const entries = Object.entries(request).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  return entries
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${String(v)}`)
    .join("&");
};

const fetchQuoteShared = (
  request: IQuoteRequest,
  linkId?: string | null,
  options?: { raasSilentOffline?: boolean },
): Promise<IQuoteResponse> => {
  const key = linkId
    ? `deferred|${linkId}|${quoteKey({
        capabilityKey: request.capabilityKey,
        payoutMethod: request.payoutMethod,
        fundingMethod: request.fundingMethod,
        amount: request.amount,
        sourceCountry: request.sourceCountry,
        destinationCountry: request.destinationCountry,
        currency: request.currency,
      })}`
    : quoteKey(request);
  const existing = inFlight.get(key);
  if (existing) return existing;
  const promise = (
    linkId
      ? getDeferredQuote(
          linkId,
          {
            capabilityKey: request.capabilityKey,
            payoutMethod: request.payoutMethod,
          },
          { capabilityKey: request.capabilityKey, anchor: request.anchor },
          { raasSilentOffline: options?.raasSilentOffline },
        )
      : getQuote(request, { raasSilentOffline: options?.raasSilentOffline })
  ).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);
  return promise;
};

interface UseQuoteResult {
  quote: IQuoteResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  isExpiringSoon: boolean;
  timeRemaining: number;
}

/**
 * Parameters for the binding quote. The quote is fetched only when a concrete
 * `capabilityKey` (the exact SKU picked from the pre-quote) and a positive
 * `amount` are available — the pricing engine requires the SKU to price + freeze
 * the quote.
 */
export interface UseQuoteParams {
  sourceCountry: string;
  destinationCountry: string;
  fundingMethod: string | null;
  payoutMethod: string | null;
  capabilityKey: string | null;
  amount: number | null;
  currency: string;
  /** Which leg `amount` is pinned to. Defaults to `send` for send-anchored links. */
  anchor?: QuoteAnchor;
  /**
   * When set, quote via `POST …/deferred-payout/quote` (sender card resolved
   * server-side). Never send cardId on this path.
   */
  linkId?: string | null;
  /** When false, skip network fetches (consumers that only need pre-quote data). */
  enabled?: boolean;
  /** ISO 4217 destination currency from the send commitment. Always sent. */
  destinationCurrency?: string;
}

/**
 * Hook to fetch a binding quote for a money transfer. The quote is fetched when
 * `capabilityKey`, `fundingMethod`, `payoutMethod` and a positive `amount` are
 * provided. Automatically refreshes the quote before it expires (with a buffer).
 *
 * Identity changes (method / SKU / amount) set loading and clear the previous
 * JWT **before** the debounce, so confirm cannot freeze a stale rail.
 * `enabled: false` pauses fetches without clearing a quote already in hand
 * (TransferPage toggles this during bind).
 */
export const useQuote = (params: UseQuoteParams): UseQuoteResult => {
  const {
    sourceCountry,
    destinationCountry,
    fundingMethod,
    payoutMethod,
    capabilityKey,
    amount,
    currency,
    anchor,
    linkId,
    enabled = true,
    destinationCurrency,
  } = params;

  const [quote, setQuote] = useState<IQuoteResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);

  const isRefreshing = useRef(false);
  const fetchGen = useRef(0);

  const fetchQuote = useCallback(async (options?: { silent?: boolean }) => {
    if (!enabled) {
      return;
    }

    if (
      !isBindingQuoteReady({
        capabilityKey,
        fundingMethod,
        payoutMethod,
        amount,
      })
    ) {
      setQuote((prev) => (prev == null ? prev : null));
      setLoading((prev) => (prev === false ? prev : false));
      setError((prev) => (prev == null ? prev : null));
      setTimeRemaining((prev) => (prev === 0 ? prev : 0));
      setIsExpiringSoon((prev) => (prev === false ? prev : false));
      return;
    }

    const gen = fetchGen.current;
    setLoading(true);
    setError(null);

    try {
      const request: IQuoteRequest = {
        sourceCountry,
        destinationCountry,
        fundingMethod: fundingMethod as string,
        payoutMethod: payoutMethod as string,
        capabilityKey: capabilityKey as string,
        amount: amount as number,
        currency,
        ...(anchor ? { anchor } : {}),
        ...(destinationCurrency ? { destinationCurrency } : {}),
      };

      const result = await fetchQuoteShared(request, linkId, {
        raasSilentOffline: options?.silent,
      });
      if (gen !== fetchGen.current) return;

      setQuote(result);

      const expiresAt = new Date(result.expiresAt).getTime();
      const now = Date.now();
      const remainingSeconds = Math.max(
        0,
        Math.floor((expiresAt - now) / 1000)
      );

      setTimeRemaining(remainingSeconds);
      setIsExpiringSoon(remainingSeconds <= WARNING_THRESHOLD_SECONDS);

      if (!isRefreshing.current) {
        safeCapture(getEvents().QUOTE_CREATED, {
          success: true,
          quote_id: result.quoteId,
          send_amount: result.sendAmount,
          receive_amount: result.receiveAmount,
          exchange_rate: result.exchangeRate,
          total_fee: result.totalFee,
          total_cost: result.totalCost,
          expires_at: result.expiresAt,
          capability_key: capabilityKey,
          funding_method: fundingMethod,
          payout_method: payoutMethod,
          source_currency: result.sourceCurrency,
          destination_currency: result.destinationCurrency,
          estimated: false,
        });
      }

      isRefreshing.current = false;
    } catch (err) {
      if (gen !== fetchGen.current) return;
      setError(err instanceof Error ? err : new Error("Failed to fetch quote"));
      setQuote(null);
      setTimeRemaining(0);
      setIsExpiringSoon(false);
      if (!isRefreshing.current) {
        safeCapture(getEvents().QUOTE_CREATED, {
          success: false,
          error: err instanceof Error ? err.message : "Quote failed",
          error_code: "QUOTE_FAILED",
          capability_key: capabilityKey,
          funding_method: fundingMethod,
          payout_method: payoutMethod,
        });
      }
      isRefreshing.current = false;
    } finally {
      if (gen === fetchGen.current) {
        setLoading(false);
      }
    }
  }, [
    enabled,
    sourceCountry,
    destinationCountry,
    fundingMethod,
    payoutMethod,
    capabilityKey,
    amount,
    currency,
    anchor,
    linkId,
    destinationCurrency,
  ]);

  // Invalidate synchronously when the quote identity changes, then debounce
  // the network fetch. TTL auto-refresh calls fetchQuote directly and must
  // not go through this path. `enabled: false` pauses without clearing.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const ready = isBindingQuoteReady({
      capabilityKey,
      fundingMethod,
      payoutMethod,
      amount,
    });
    const ui = quoteUiOnIdentityChange(ready);
    fetchGen.current += 1;
    if (ui.clearQuote) {
      setQuote(null);
      setError(null);
      setTimeRemaining(0);
      setIsExpiringSoon(false);
    }
    setLoading(ui.loading);

    if (!ready) {
      return;
    }

    const timeoutId = setTimeout(() => {
      void fetchQuote();
    }, BINDING_QUOTE_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [enabled, fetchQuote, capabilityKey, fundingMethod, payoutMethod, amount]);

  // Timer para actualizar timeRemaining y auto-refresh cuando expira.
  useEffect(() => {
    if (!enabled || !quote?.expiresAt) {
      return;
    }

    const intervalId = setInterval(() => {
      const expiresAt = new Date(quote.expiresAt).getTime();
      const now = Date.now();
      const remainingSeconds = Math.max(
        0,
        Math.floor((expiresAt - now) / 1000)
      );

      setTimeRemaining(remainingSeconds);
      setIsExpiringSoon(remainingSeconds <= WARNING_THRESHOLD_SECONDS);

      if (
        remainingSeconds <= EXPIRATION_BUFFER_SECONDS &&
        !isRefreshing.current
      ) {
        isRefreshing.current = true;
        fetchQuote({ silent: true });
      }

      if (remainingSeconds <= 0 && !isRefreshing.current) {
        isRefreshing.current = true;
        fetchQuote({ silent: true });
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [enabled, quote?.expiresAt, fetchQuote]);

  return {
    quote,
    loading,
    error,
    refetch: fetchQuote,
    isExpiringSoon,
    timeRemaining,
  };
};
